/**
 * User session helpers for main App.
 */
import { setCookie } from '@/helpers';
import { loginUrl, logoutUrl } from '@/helpers/muusa';
import {
    phaseAnonymous,
    phaseExpired,
    phaseAuthenticated
} from '@/store/ux';
import { TribeCommunityUrl } from '@/plugins/tribe.client.js';

export default {
    methods: {
        /**
        ** Get user session by requesting /svc/hello
        **/
        getUserSession: async function (force) {
            let session = this.$store.getters['user/getSession'];

            // current status
            const anonymous = this.$store.getters['user/isAnonymous'];
            // new status
            const _isAuthenticated = this.isUserSessionAuthenticated(session);

            return new Promise((resolve, reject) => {

                if (force || (!_isAuthenticated && !anonymous) || this.isUserSessionAboutToExpire(session)) {

                    // request from server
                    this.$store.dispatch('user/fetchUserSession')
                        .then((userInfo) => {
                            session = this.setUserSession(userInfo);
                            // console.log("session", userInfo, userInfo.Info);
                            if (session) {
                                // set loginPhase to authenticated, if phase was lower
                                if (this.$store.getters['ux/getLoginPhase'] < phaseAuthenticated) {
                                    this.$store.commit('ux/setLoginPhase', phaseAuthenticated);
                                }
                                resolve(session);
                            }
                            else {
                                reject(new Error("session expired"));
                            }
                        })
                        .catch(() => {
                            this.expireUserSession();
                            reject(new Error("session expired"));
                        });
                }
                // unless forced, read cached copy from vuex store
                else if (_isAuthenticated) {
                    this.$eventBus.$emit('auth-change', {authenticated: true});

                    // set loginPhase to authenticated, if phase was lower
                    if (this.$store.getters['ux/getLoginPhase'] < phaseAuthenticated) {
                        this.$store.commit('ux/setLoginPhase', phaseAuthenticated);
                    }
                    if (session && session.UserId) {
                        // update to store only when forced, or store value is unset
                        if (force || this.$store.getters['user/getUserId'] !== session.UserId) {
                            this.$store.commit('user/setUserId', session.UserId);
                        }
                    }
                    resolve(session);
                }
                else {
                    reject(new Error("not authenticated"));
                }
            });
        },

        /**
        ** Set session expiration time.
        ** User info is saved into global vuex store (using sessionStore persistence layer).
        **/
        setUserSession: function (userInfo) {
            // set Tribe flags
            if (this.$store.getters['ux/getCommunityAllowAnon'] !== userInfo.CommunityAllowAnon) {
                this.$store.commit('ux/setCommunityAllowAnon', userInfo.CommunityAllowAnon);
            }
            if (this.$store.getters['ux/getCommunityShowNavigation'] !== userInfo.CommunityShowNavigation) {
                this.$store.commit('ux/setCommunityShowNavigation', userInfo.CommunityShowNavigation);
            }

            if (!userInfo || !userInfo.UserId || !userInfo.Info || (userInfo.Info.search(/IsAuthenticated/) === -1)) {
                this.expireUserSession();
                return false;
            }

            // check if UserId changed, reset user store
            const oldUserId = this.$store.getters['user/getUserId'];
            if (oldUserId && oldUserId !== userInfo.UserId) {
                this.$store.commit('user/RESET');
            }

            // save the session to vuex store
            try {
                // commit session to store, if changed
                if (!_.isEqual(userInfo, this.$store.getters['user/getSession'])) {
                    this.$store.commit('user/setSession', userInfo);
                }
                if (oldUserId !== userInfo.UserId) {
                    this.$store.commit('user/setUserId', userInfo.UserId);
                }

                // if profile is not yet fetched, change membership level > 0 to indicate user is not going to be anon
                if (this.$store.getters['user/getMembershipLevel'] === 0) {
                    // authentication in process - real membership level will be will be set in user store
                    this.$store.commit('user/setMembershipLevel', 500);
                }

                // hide registration tooltip (until next store reset)
                this.$store.commit('ux/setShowRegistrationInfo', false); // hide tooltip

                this.$eventBus.$emit('auth-change', {authenticated: true});
                return userInfo;
            }
            catch (error) {
                // browser has no session storage?
                return false;
            }
        },

        /**
         * Main App relies on this to track `isAuthenticated` state.
         */
        isUserSessionAuthenticated: function (session) {
            if (!process.client) {
                // render only anonymous view in nuxt server environment
                return false;
            }
            if (!session) {
                session = this.$store.getters['user/getSession'];
            }
            if (!session || !session.UserId || !session.SessionExpires) {
                return false;
            }
            // Check whether the current time is past the access token's expiry time
            const expiresAt = new Date(session.SessionExpires);
            if (!expiresAt) {
                return false;
            }
            const now = new Date().getTime();
            if (expiresAt > now) {
                return true;
            }
            return false;
        },

        /**
        * Checks if session should be renewed.
        * This would be 15 minutes before expiry with 9 minute check interval.
        * Returns false in all other cases.
        */
        isUserSessionAboutToExpire: function (session) {
            if (!process.client) {
                return false;
            }
            if (!session || !session.UserId || !session.SessionExpires) {
                return false;
            }
            // Check whether the current time is past the access token's expiry time
            const expiresAt = new Date(session.SessionExpires);
            if (!expiresAt) {
                return false;
            }
            const now = new Date().getTime();
            const minutesLeft = (now - expiresAt) / -60000;
            // console.log(`This session will expire at ${expiresAt.toISOString()} * T-${minutesLeft.toFixed(1)} min`);
            if (minutesLeft < 15) {
                return true;
            }
            return false;
        },

        /**
        ** Clear session storage and cookies
        **/
        clearUserSession: function () {
            // first delete Tribe community cookie
            // this.clearTribeSession();

            // clear user data
            if (this.$store.getters['user/getUserId']
                || this.$store.getters['user/getSession']
                || this.$store.getters['user/getProfile']
            ) {
                this.$store.commit('user/RESET');
            }

            // clear ux data, but retain session expired info, setting default phase to anonymous
            const loginPhase = (this.$store.getters['ux/getLoginPhase'] === phaseExpired) ? phaseExpired : phaseAnonymous;
            this.$store.commit('ux/RESET');
            this.$store.commit('ux/setLoginPhase', loginPhase);

            // delete cookies on logout
            setCookie('.ASPXAUTH', '', 0);
            setCookie('ss-id', '', 0);
            setCookie('ss-pid', '', 0);
        },

        /**
         * Clear Tribe community cookies.
         */
        clearTribeSession: function () {
            this.$axios
                .delete(`${TribeCommunityUrl}/api/v1/sessions`)
                // .then((response) => {
                //     console.log(response);
                // })
                .catch((error) => {
                    console.warn(error);
                });
        },

        /**
        ** Expire session and clear user profile information from localStorages.
        **/
        expireUserSession: function () {
            // first delete Tribe community cookie
            // this.clearTribeSession();

            // clear user data
            if (this.$store.getters['user/getUserId']
                || this.$store.getters['user/getSession']
                || this.$store.getters['user/getProfile']
            ) {
                this.$store.commit('user/RESET');
            }

            if (this.$store.getters['ux/getLoginPhase'] < phaseAuthenticated) {
                // anonymous / expired user - do nothing
                return;
            }

            this.$store.commit('ux/setLoginPhase', phaseExpired);

            // emit signal to hide authorized content
            this.$eventBus.$emit('auth-change', {authenticated: false});
        },

        /**
        * Login by pointing the browser to <BASE URL>/login.
        * The backend gives the user session cookies.
        * Save note that the user is logging in (via ext service) to store.
        * Login path ReturnUrl param will be based on current route.
        * Info pages should return to root. Router can set this meta data.
        */
        onLogin: async function () {
            // direct to /svc/auth/auth0 to let muusa handle authentication.
            const meta = this.$route && this.$route.meta;
            const url = loginUrl(meta && meta.loginReturnUrl);
            url && window.location.assign(url);
        },

        /**
        * Note that at logout, before entering the new url, and the browser navigates
        * within the app, new session is fetched before SVC backend invalidates the cookies.
        * This would lead to the user being "logged in" with invalid cookies.
        */
        onLogout: function () {
            this.clearUserSession();
            const url = logoutUrl();
            // this.$eventBus.$emit('auth-change', {authenticated: false});
            url && window.location.replace(url);
        }
    }
};
